/**
 * Meilisearch client library
 * Communicates with Meilisearch through Next.js API proxy
 * The API key is kept server-side for security
 */

import { CATEGORY_DISPLAY_ORDER, PLAYLIST_DISPLAY_ORDER } from './constants'

/** Default timeout for Meilisearch requests (10 seconds) */
const MEILISEARCH_TIMEOUT_MS = 10000

/**
 * Escape special characters in filter values to prevent injection
 * Meilisearch uses double quotes for string values in filters
 */
function escapeFilterValue(value: string): string {
  // Escape backslashes first, then double quotes
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

/**
 * User object from Meilisearch listing
 */
export interface MeiliUser {
  id: string
  name: string
  avatar: string | null
  bio: string | null
}

/**
 * Meilisearch listing document structure for video listings (category_id = 1)
 * Based on the example data provided in the issue
 */
export interface MeiliListing {
  listing_id: string
  category_id: number
  status: string
  created_at: string
  updated_at: string
  user: MeiliUser
  categories: string
  videotitle: string
  videodescription: string
  videocategory: string
  video: number | null
  /** Thumbnail URL from Meilisearch (pre-signed S3 URL). Falls back to API if missing. */
  thumbnail_url?: string
}

/**
 * Playlist listings data structure (as stored in Meilisearch)
 */
export interface PlaylistListingsObject {
  videos: Array<{
    listing_id: string
    order: number
  }>
}

/**
 * Meilisearch playlist document structure for playlist listings (category_id = 2)
 */
export interface MeiliPlaylist {
  listing_id: string
  category_id: number
  status: string
  created_at: string
  updated_at: string
  user: MeiliUser
  categories: string
  /** Playlist title (field ID 7) - note: field name has double 't' in database */
  playlisttittle: string
  /** Video listing IDs (field ID 8) - can be JSON string or parsed object from Meilisearch */
  playlistlistings: string | PlaylistListingsObject
  /** Category for the playlist (field ID 3) */
  videocategory?: string
}

/**
 * Meilisearch file document structure for file listings (category_id = 3)
 */
export interface MeiliFile {
  listing_id: string
  category_id: number
  status: string
  created_at: string
  updated_at: string
  user: MeiliUser
  categories: string
  /** File name (field ID 9) */
  filename: string
  /** Digital file URL (field ID 10) */
  digitalfile?: string
}

/**
 * Search response from Meilisearch
 */
export interface MeiliSearchResponse<T> {
  hits: T[]
  query: string
  processingTimeMs: number
  limit: number
  offset: number
  estimatedTotalHits: number
  facetDistribution?: Record<string, Record<string, number>>
}

/**
 * Category extracted from listings
 */
export interface MeiliCategory {
  id: number
  name: string
  count: number
}

/**
 * Search options for Meilisearch queries
 */
interface SearchOptions {
  limit?: number
  offset?: number
  filter?: string
  sort?: string[]
  facets?: string[]
  attributesToSearchOn?: string[]
}

/**
 * Make a search request through the Next.js proxy with timeout
 */
async function searchProxy<T>(options: {
  query?: string
  limit?: number
  offset?: number
  filter?: string
  sort?: string[]
  facets?: string[]
  attributesToSearchOn?: string[]
}): Promise<MeiliSearchResponse<T>> {
  // Create AbortController for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), MEILISEARCH_TIMEOUT_MS)

  try {
    const response = await fetch('/api/meilisearch/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      signal: controller.signal,
      body: JSON.stringify({
        query: options.query || '',
        limit: options.limit || 20,
        offset: options.offset || 0,
        filter: options.filter,
        sort: options.sort,
        facets: options.facets,
        attributesToSearchOn: options.attributesToSearchOn,
      }),
    })

    if (!response.ok) {
      throw new Error(`Search failed: ${response.status}`)
    }

    const data = await response.json()
    
    return {
      hits: data.hits || [],
      query: data.query || '',
      processingTimeMs: data.processingTimeMs || 0,
      limit: data.limit || options.limit || 20,
      offset: data.offset || options.offset || 0,
      estimatedTotalHits: data.estimatedTotalHits || data.hits?.length || 0,
      facetDistribution: data.facetDistribution,
    }
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Search video listings with optional filters
 * Only returns video listings (category_id = 1), excluding playlists
 */
export async function searchListings(
  query: string = '',
  options: SearchOptions = {}
): Promise<MeiliSearchResponse<MeiliListing>> {
  // Add category_id = 1 filter to only return video listings
  const baseFilter = 'category_id = 1'
  const combinedFilter = options.filter
    ? `${baseFilter} AND (${options.filter})`
    : baseFilter

  return searchProxy<MeiliListing>({
    query,
    ...options,
    filter: combinedFilter,
  })
}

/**
 * Get a single video listing by ID
 * Uses a single efficient query with filter instead of fallback approach
 * Only returns video listings (category_id = 1), not playlists
 */
export async function getListingById(listingId: string): Promise<MeiliListing | null> {
  try {
    // Validate listing ID format (should be numeric string or alphanumeric)
    if (!listingId || !/^[a-zA-Z0-9_-]+$/.test(listingId)) {
      console.warn('Invalid listing ID format:', listingId)
      return null
    }

    // Escape the listing ID to prevent filter injection
    const escapedId = escapeFilterValue(listingId)
    
    // Use filter-based search for efficiency - single query only
    // Filter by category_id = 1 to only return video listings
    const response = await searchProxy<MeiliListing>({
      query: listingId, // Include as query for better matching
      filter: `listing_id = "${escapedId}" AND category_id = 1`,
      limit: 1,
    })
    
    // Return exact match only
    return response.hits[0] || null
  } catch (error) {
    console.error('Error fetching listing by ID:', error)
    return null
  }
}

/**
 * Get all unique categories from video listings
 * Categories are sorted according to CATEGORY_DISPLAY_ORDER.
 * Any categories not in the defined order appear at the end.
 * Only counts video listings (category_id = 1), excluding playlists.
 * The "Ads" category is excluded from the results.
 */
export async function getCategories(): Promise<MeiliCategory[]> {
  try {
    const response = await searchProxy<MeiliListing>({
      query: '',
      limit: 0,
      filter: 'category_id = 1',
      facets: ['videocategory'],
    })
    
    const facetDistribution = response.facetDistribution?.videocategory || {}
    
    const categories: MeiliCategory[] = Object.entries(facetDistribution)
      .filter(([name]) => name !== 'Ads') // Exclude "Ads" category
      .map(([name, count], index) => ({
        id: index + 1,
        name,
        count: count as number,
      }))
    
    // Sort categories based on CATEGORY_DISPLAY_ORDER
    // Categories not in the order list appear at the end
    const orderMap = new Map(
      CATEGORY_DISPLAY_ORDER.map((name, index) => [name.toLowerCase(), index])
    )
    
    categories.sort((a, b) => {
      const orderA = orderMap.get(a.name.toLowerCase())
      const orderB = orderMap.get(b.name.toLowerCase())
      
      // Both are in the order list - sort by their defined order
      if (orderA !== undefined && orderB !== undefined) {
        return orderA - orderB
      }
      // Only a is in the order list - a comes first
      if (orderA !== undefined) {
        return -1
      }
      // Only b is in the order list - b comes first
      if (orderB !== undefined) {
        return 1
      }
      // Neither is in the order list - sort alphabetically
      return a.name.localeCompare(b.name)
    })
    
    return categories
  } catch (error) {
    console.error('Error fetching categories:', error)
    return []
  }
}

/**
 * Get listings by category
 * Only returns video listings (category_id = 1), excluding playlists
 */
export async function getListingsByCategory(
  category: string,
  options: {
    limit?: number
    offset?: number
  } = {}
): Promise<MeiliSearchResponse<MeiliListing>> {
  // Base filter: only video listings (category_id = 1), not playlists
  let filter = 'category_id = 1'
  
  // Add video category filter if specified
  if (category && category !== 'All') {
    const escapedCategory = escapeFilterValue(category)
    filter = `${filter} AND videocategory = "${escapedCategory}"`
  }
  
  return searchProxy<MeiliListing>({
    query: '',
    ...options,
    filter,
  })
}

/**
 * Get related listings based on category
 * Only returns video listings (category_id = 1), excluding playlists
 */
export async function getRelatedListings(
  currentListingId: string,
  category: string,
  limit: number = 8
): Promise<MeiliListing[]> {
  try {
    // Escape values to prevent filter injection
    const escapedCategory = escapeFilterValue(category)
    const escapedListingId = escapeFilterValue(currentListingId)
    
    // Base filter: only video listings (category_id = 1), excluding current listing
    let filter = `category_id = 1 AND listing_id != "${escapedListingId}"`
    
    // Add video category filter if specified
    if (category) {
      filter = `${filter} AND videocategory = "${escapedCategory}"`
    }
    
    const response = await searchProxy<MeiliListing>({
      query: '',
      filter,
      limit,
    })
    
    return response.hits
  } catch (error) {
    console.error('Error fetching related listings:', error)
    return []
  }
}

/**
 * Get all listings with optional category filter and search query.
 * Used for Studio page to show videos with infinite scroll.
 * 
 * @param options - Optional search options
 * @param options.limit - Maximum number of results to return (default: 20)
 * @param options.offset - Number of results to skip for pagination
 * @param options.category - Video category to filter by. Pass 'All' or omit to show all categories
 * @param options.query - Search query to filter listings by title/description
 * @returns Promise with paginated Meilisearch response containing listings
 */
export async function getStudioListings(
  options: {
    limit?: number
    offset?: number
    category?: string
    query?: string
  } = {}
): Promise<MeiliSearchResponse<MeiliListing>> {
  // Build filter for category if specified and not 'All'
  let filter: string | undefined = undefined
  
  if (options.category && options.category !== 'All') {
    const escapedCategory = escapeFilterValue(options.category)
    filter = `videocategory = "${escapedCategory}"`
  }
  
  return searchListings(options.query || '', {
    limit: options.limit,
    offset: options.offset,
    filter,
  })
}

/**
 * Search playlists with optional filters
 * Playlists are listings with category_id = 2
 */
export async function searchPlaylists(
  query: string = '',
  options: SearchOptions = {}
): Promise<MeiliSearchResponse<MeiliPlaylist>> {
  const baseFilter = 'category_id = 2'
  const combinedFilter = options.filter 
    ? `${baseFilter} AND (${options.filter})`
    : baseFilter
  
  return searchProxy<MeiliPlaylist>({
    query,
    ...options,
    filter: combinedFilter,
  })
}

/**
 * Get all playlists, optionally filtered by video category
 * @param category - Optional category to filter playlists by
 * @param options - Pagination options
 */
export async function getPlaylists(
  category?: string,
  options: {
    limit?: number
    offset?: number
  } = {}
): Promise<MeiliSearchResponse<MeiliPlaylist>> {
  let filter = 'category_id = 2'
  
  if (category && category !== 'All') {
    const escapedCategory = escapeFilterValue(category)
    filter = `${filter} AND videocategory = "${escapedCategory}"`
  }
  
  return searchProxy<MeiliPlaylist>({
    query: '',
    limit: options.limit || 50,
    offset: options.offset || 0,
    filter,
  })
}

/**
 * Get a single playlist by listing ID
 */
export async function getPlaylistById(listingId: string): Promise<MeiliPlaylist | null> {
  try {
    if (!listingId || !/^[a-zA-Z0-9_-]+$/.test(listingId)) {
      console.warn('Invalid playlist ID format:', listingId)
      return null
    }

    const escapedId = escapeFilterValue(listingId)
    
    const response = await searchProxy<MeiliPlaylist>({
      query: listingId,
      filter: `listing_id = "${escapedId}" AND category_id = 2`,
      limit: 1,
    })
    
    return response.hits[0] || null
  } catch (error) {
    console.error('Error fetching playlist by ID:', error)
    return null
  }
}

/**
 * Get multiple video listings by their IDs
 * Used to populate playlist video details
 * Only returns video listings (category_id = 1), not playlists
 */
export async function getListingsByIds(listingIds: string[]): Promise<MeiliListing[]> {
  if (!listingIds.length) return []
  
  // Limit the number of IDs per Meilisearch request to avoid exceeding filter size limits
  const MAX_IDS_PER_REQUEST = 500
  
  try {
    // If within limit, do a single request
    if (listingIds.length <= MAX_IDS_PER_REQUEST) {
      const escapedIds = listingIds.map(id => `"${escapeFilterValue(id)}"`).join(', ')
      const filter = `category_id = 1 AND listing_id IN [${escapedIds}]`
      
      const response = await searchProxy<MeiliListing>({
        query: '',
        filter,
        limit: listingIds.length,
      })
      
      return response.hits
    }
    
    // Otherwise, batch the requests
    const chunks: string[][] = []
    for (let i = 0; i < listingIds.length; i += MAX_IDS_PER_REQUEST) {
      chunks.push(listingIds.slice(i, i + MAX_IDS_PER_REQUEST))
    }

    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `getListingsByIds: received ${listingIds.length} IDs, batching into ${chunks.length} Meilisearch requests to avoid filter size limits`
      )
    }

    const results = await Promise.all(
      chunks.map(async (idsChunk) => {
        const escapedIds = idsChunk.map(id => `"${escapeFilterValue(id)}"`).join(', ')
        const filter = `category_id = 1 AND listing_id IN [${escapedIds}]`

        const response = await searchProxy<MeiliListing>({
          query: '',
          filter,
          limit: idsChunk.length,
        })

        return response.hits
      })
    )
    
    return results.flat()
  } catch (error) {
    console.error('Error fetching listings by IDs:', error)
    return []
  }
}

/**
 * Get playlists grouped by category for sidebar display.
 * Playlists within each category are sorted according to PLAYLIST_DISPLAY_ORDER.
 */
export async function getPlaylistsByCategory(): Promise<Record<string, MeiliPlaylist[]>> {
  try {
    const response = await getPlaylists(undefined, { limit: 100 })
    
    const grouped: Record<string, MeiliPlaylist[]> = {}
    
    for (const playlist of response.hits) {
      const category = playlist.videocategory || 'Uncategorized'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(playlist)
    }
    
    // Sort playlists within each category based on PLAYLIST_DISPLAY_ORDER
    const orderMap = new Map(
      PLAYLIST_DISPLAY_ORDER.map((name, index) => [name.toLowerCase(), index])
    )
    
    for (const category of Object.keys(grouped)) {
      grouped[category].sort((a, b) => {
        const orderA = orderMap.get(a.playlisttittle?.toLowerCase() ?? '')
        const orderB = orderMap.get(b.playlisttittle?.toLowerCase() ?? '')
        
        // Both are in the order list - sort by their defined order
        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB
        }
        // Only a is in the order list - a comes first
        if (orderA !== undefined) {
          return -1
        }
        // Only b is in the order list - b comes first
        if (orderB !== undefined) {
          return 1
        }
        // Neither is in the order list - sort alphabetically
        return (a.playlisttittle ?? '').localeCompare(b.playlisttittle ?? '')
      })
    }
    
    return grouped
  } catch (error) {
    console.error('Error fetching playlists by category:', error)
    return {}
  }
}

/**
 * Search video listings for playlist selection
 * Only returns video listings (category_id = 1), not playlists
 */
export async function searchVideosForPlaylist(
  query: string = '',
  options: {
    limit?: number
    offset?: number
    category?: string
  } = {}
): Promise<MeiliSearchResponse<MeiliListing>> {
  let filter = 'category_id = 1'
  
  if (options.category && options.category !== 'All') {
    const escapedCategory = escapeFilterValue(options.category)
    filter = `${filter} AND videocategory = "${escapedCategory}"`
  }
  
  return searchProxy<MeiliListing>({
    query,
    limit: options.limit || 20,
    offset: options.offset || 0,
    filter,
  })
}

/**
 * Get new videos sorted by creation date (newest first).
 * Only returns video listings (category_id = 1), excluding playlists.
 * Excludes videos in the "Ads" category.
 * 
 * @param options - Pagination options
 * @param options.limit - Maximum number of results to return (default: 20)
 * @param options.offset - Number of results to skip for pagination
 * @returns Promise with paginated Meilisearch response containing listings sorted by created_at desc
 */
export async function getNewVideos(
  options: {
    limit?: number
    offset?: number
  } = {}
): Promise<MeiliSearchResponse<MeiliListing>> {
  return searchProxy<MeiliListing>({
    query: '',
    limit: options.limit || 20,
    offset: options.offset || 0,
    filter: 'category_id = 1 AND videocategory != "Ads"',
    sort: ['created_at:desc'],
  })
}

/**
 * Search file listings with optional filters
 * Files are listings with category_id = 3
 * Searches across all Meilisearch searchable attributes (including filename)
 */
export async function searchFiles(
  query: string = '',
  options: SearchOptions = {}
): Promise<MeiliSearchResponse<MeiliFile>> {
  const baseFilter = 'category_id = 3'
  const combinedFilter = options.filter 
    ? `${baseFilter} AND (${options.filter})`
    : baseFilter
  
  return searchProxy<MeiliFile>({
    query,
    ...options,
    filter: combinedFilter,
  })
}

/**
 * Get all file listings with optional search query.
 * Files are listings with category_id = 3
 * 
 * @param options - Optional search options
 * @param options.limit - Maximum number of results to return (default: 20)
 * @param options.offset - Number of results to skip for pagination
 * @param options.query - Search query to filter files by filename
 * @returns Promise with paginated Meilisearch response containing file listings
 */
export async function getFiles(
  options: {
    limit?: number
    offset?: number
    query?: string
  } = {}
): Promise<MeiliSearchResponse<MeiliFile>> {
  return searchFiles(options.query || '', {
    limit: options.limit || 20,
    offset: options.offset || 0,
    sort: ['created_at:desc'],
  })
}

/**
 * Get a single file listing by ID
 */
export async function getFileById(listingId: string): Promise<MeiliFile | null> {
  try {
    if (!listingId || !/^[a-zA-Z0-9_-]+$/.test(listingId)) {
      console.warn('Invalid file ID format:', listingId)
      return null
    }

    const escapedId = escapeFilterValue(listingId)
    
    const response = await searchProxy<MeiliFile>({
      query: listingId,
      filter: `listing_id = "${escapedId}" AND category_id = 3`,
      limit: 1,
    })
    
    return response.hits[0] || null
  } catch (error) {
    console.error('Error fetching file by ID:', error)
    return null
  }
}
